import { Component, OnInit } from '@angular/core';
import { MerchantService } from 'src/app/services/merchant/merchant.service';
import { AuthService } from 'src/app/services/auth/auth.service';

/**
 * TARIFARIO DE LIQUIDACIÓN — la tabla única de plazos de la que salen el nombre del plazo y la
 * comisión al cargar las condiciones de un comercio (Mario, 07-oct-2026).
 *
 * Versionada: una versión nueva rige desde su fecha y cierra la vigente; las versiones de condiciones
 * guardan qué tabla usaron. Mientras no haya ninguna en la base, rige la provisional del 05-oct (V1).
 * Reglas (las mismas en el servidor): días únicos desde 1, nombre, comisión entre 0 y 5 %.
 */
@Component({
  selector: 'app-tarifario',
  templateUrl: './tarifario.component.html'
})
export class TarifarioComponent implements OnInit {
  readonly TOPE = 5;

  cargando = true;
  error = '';
  versiones: any[] = [];

  // ── versión nueva ──
  creando = false;
  guardando = false;
  errorGuardar = '';
  aviso = '';
  nueva: any = this.nuevaVacia();

  /** Una tabla por tipo de contrato (Mario, 11-oct). */
  tipo: 'COMPRA_DEUDA' | 'COBRANZA' = 'COMPRA_DEUDA';

  constructor(private merchants: MerchantService, public _auth: AuthService) { }

  /** PAR-07 (Mario, 12-oct): versiona el tarifario quien puede aprobar condiciones (la gerente de finanzas o el director). */
  get puedeEditar(): boolean { return this._auth.hasPermission('config.comercios.terms.approve'); }

  verTipo = (t: 'COMPRA_DEUDA' | 'COBRANZA'): void => { if (this.tipo === t) return; this.tipo = t; this.creando = false; this.aviso = ''; this.cargar(); };

  ngOnInit(): void { this.cargar(); }

  get vigente(): any {
    const ahora = Date.now();
    return this.versiones.find(v => new Date(v.version.date_init ?? 0).getTime() <= ahora
      && (!v.version.date_end || new Date(v.version.date_end).getTime() > ahora)) || this.versiones[0] || null;
  }

  cargar = async (): Promise<void> => {
    this.cargando = true; this.error = '';
    try { this.versiones = await this.merchants.getRateTables(this.tipo); }
    catch (e: any) { this.error = e?.error?.msg || 'No se pudo leer el tarifario.'; }
    finally { this.cargando = false; }
  };

  private manana(): string {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private nuevaVacia(): any { return { desde: this.manana(), provisional: false, nota: '', rows: [] as any[] }; }

  abrirNueva = (): void => {
    const base = this.vigente;
    this.nueva = this.nuevaVacia();
    this.nueva.rows = (base?.rows || []).map((r: any) => ({ days: r.days, label: r.label, commission_value: r.commission_value }));
    if (!this.nueva.rows.length) this.agregarFila();
    this.creando = true; this.errorGuardar = ''; this.aviso = '';
  };

  agregarFila = (): void => { this.nueva.rows.push({ days: null, label: '', commission_value: null }); };
  quitarFila = (i: number): void => { this.nueva.rows.splice(i, 1); };

  /** Lo mismo que valida el servidor, dicho antes de enviar. */
  get errores(): string[] {
    const e: string[] = [];
    if (!this.nueva.desde) e.push('Falta la fecha desde la que rige');
    if (!this.nueva.rows.length) e.push('La tabla necesita al menos un plazo');
    const vistos = new Set<number>();
    this.nueva.rows.forEach((r: any, i: number) => {
      const d = Number(r.days), c = Number(r.commission_value);
      if (!Number.isInteger(d) || d < 1) e.push(`Fila ${i + 1}: los días deben ser un entero desde 1`);
      else if (vistos.has(d)) e.push(`Fila ${i + 1}: el plazo de ${d} días está repetido`);
      vistos.add(d);
      if (!String(r.label || '').trim()) e.push(`Fila ${i + 1}: falta el nombre del plazo`);
      if (r.commission_value === null || r.commission_value === '' || !(c >= 0) || c > this.TOPE) {
        e.push(`Fila ${i + 1}: la comisión debe estar entre 0 y ${this.TOPE} %`);
      }
    });
    return e;
  }

  guardar = async (): Promise<void> => {
    if (this.errores.length) return;
    this.guardando = true; this.errorGuardar = '';
    try {
      // Fecha local a medianoche de Venezuela (UTC-4).
      const desde = new Date(`${this.nueva.desde}T00:00:00-04:00`).toISOString();
      const r = await this.merchants.createRateTable({ ...this.nueva, desde, business_model: this.tipo });
      this.aviso = `Se creó ${r.tarifario?.version?.code}. Rige desde el ${this.nueva.desde}.`;
      this.creando = false;
      await this.cargar();
    } catch (e: any) {
      this.errorGuardar = e?.error?.msg || 'No se pudo crear la versión.';
    } finally { this.guardando = false; }
  };

  textoDias = (d: number): string => (d === 1 ? '24 horas' : `${d} días`);
}
